import React, {useState, useEffect} from 'react';
import {
    Bot,
    Search,
    Loader2,
    AlertTriangle,
    RefreshCw,
    Check,
    X,
    Upload,
    Link as LinkIcon,
    Trash2,
    Filter,
    CheckCircle,
    XCircle,
    Image
} from 'lucide-react';

const ViewMaktabPage = () => {
    const [bots, setBots] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [fileInputs, setFileInputs] = useState({});
    const [linkInputs, setLinkInputs] = useState({});
    const [botUpdating, setBotUpdating] = useState({});
    const [filterSect, setFilterSect] = useState('');

    const allowedFileTypes = [
        'application/pdf',
        'text/csv',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain'
    ];

    useEffect(() => {
        fetchBots();
    }, []);

    const fetchBots = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const accessToken = localStorage.getItem('accessToken');

            const response = await fetch('https://api.digitalaalim.com/admin/all-bots/', {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch bots: ${response.statusText}`);
            }

            const data = await response.json();
            // Sort the bots by creation date, newest first
            const sortedBots = data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            setBots(sortedBots);
        } catch (error) {
            console.error('Error fetching bots:', error);
            setError(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const approveBot = async (botId) => {
        try {
            const accessToken = localStorage.getItem('accessToken');

            const response = await fetch(`https://api.digitalaalim.com/admin/approve-bot/${botId}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to approve bot: ${response.statusText}`);
            }

            // Update the bot in the state
            setBots(prevBots =>
                prevBots.map(bot =>
                    bot.bot_id === botId
                        ? {...bot, approved: true}
                        : bot
                )
            );

            alert("Bot approved successfully");
        } catch (error) {
            console.error('Error approving bot:', error);
            alert(`Failed to approve bot: ${error.message}`);
        }
    };

    const suspendBot = async (botId) => {
        try {
            const accessToken = localStorage.getItem('accessToken');

            const response = await fetch(`https://api.digitalaalim.com/admin/disapprove_chatbot/${botId}`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to suspend bot: ${response.statusText}`);
            }

            // Update the bot in the state
            setBots(prevBots =>
                prevBots.map(bot =>
                    bot.bot_id === botId
                        ? {...bot, approved: false}
                        : bot
                )
            );

            alert("Bot suspended successfully");
        } catch (error) {
            console.error('Error suspending bot:', error);
            alert(`Failed to suspend bot: ${error.message}`);
        }
    };

    const handleLinkChange = (event, botId) => {
        setLinkInputs({
            ...linkInputs,
            [botId]: event.target.value
        });
    };

    const handleFileChange = (event, botId) => {
        const files = Array.from(event.target.files);
        const validFiles = files.filter(file => allowedFileTypes.includes(file.type));

        if (validFiles.length !== files.length) {
            alert('Only PDF, CSV, DOCX, and TXT files are allowed.');
        }

        setFileInputs({
            ...fileInputs,
            [botId]: validFiles
        });
    };

    const handleFileDelete = (botId, fileIndex) => {
        const updatedFiles = [...fileInputs[botId]];
        updatedFiles.splice(fileIndex, 1);
        setFileInputs({
            ...fileInputs,
            [botId]: updatedFiles
        });
    };

    const updateBot = async (botId) => {
        setBotUpdating(prev => ({...prev, [botId]: true}));

        try {
            const accessToken = localStorage.getItem('accessToken');

            const formData = new FormData();
            if (fileInputs[botId]) {
                fileInputs[botId].forEach(file => {
                    formData.append('files', file);
                });
            }
            if (linkInputs[botId]) {
                formData.append('links', linkInputs[botId]);
            }
            formData.append('bot_id', botId);

            const response = await fetch('https://api.digitalaalim.com/admin/madrasah_update_model/', {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
                body: formData
            });

            if (!response.ok) {
                throw new Error(`Failed to update bot: ${response.statusText}`);
            }

            alert("Bot updated successfully");

            // Clear inputs after successful update
            setFileInputs(prev => ({...prev, [botId]: []}));
            setLinkInputs(prev => ({...prev, [botId]: ''}));
        } catch (error) {
            console.error('Error updating bot:', error);
            alert(`Failed to update bot: ${error.message}`);
        } finally {
            setBotUpdating(prev => ({...prev, [botId]: false}));
        }
    };

    // Filter bots by search term and sect
    const filteredBots = bots.filter(bot => {
        const matchesSearch =
            bot.bot_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            bot.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            bot.sect.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesSect = filterSect ? bot.sect === filterSect : true;

        return matchesSearch && matchesSect;
    });

    // Get unique sects for filter
    const sects = [...new Set(bots.map(bot => bot.sect))];

    return (
        <div className="space-y-8">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
                    <Bot className="text-blue-600" size={32}/>
                    Maktab Chatbots
                </h1>
                <p className="mt-2 text-gray-600">
                    Manage, approve, and update Maktab chatbots
                </p>
            </div>

            {/* Filters and search */}
            <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                    <div className="relative w-full sm:w-64">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search size={16} className="text-gray-400"/>
                        </div>
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search bots..."
                            className="pl-10 w-full border border-gray-300 rounded-md py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div className="flex items-center gap-2 w-full sm:w-auto">
                        <div className="inline-flex items-center">
                            <Filter size={14} className="text-gray-500 mr-1"/>
                            <select
                                value={filterSect}
                                onChange={(e) => setFilterSect(e.target.value)}
                                className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">All Sects</option>
                                {sects.map(sect => (
                                    <option key={sect} value={sect}>{sect}</option>
                                ))}
                            </select>
                        </div>

                        <button
                            onClick={fetchBots}
                            className="inline-flex items-center px-3 py-1 text-sm font-medium text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
                        >
                            <RefreshCw size={14} className="mr-1"/>
                            Refresh
                        </button>
                    </div>
                </div>
            </div>

            {/* Bots List */}
            {isLoading ? (
                <div className="flex justify-center items-center h-64">
                    <Loader2 size={40} className="animate-spin text-blue-600"/>
                </div>
            ) : error ? (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
                    <div className="flex items-center">
                        <AlertTriangle size={24} className="text-red-500 mr-3"/>
                        <div>
                            <h3 className="text-red-800 font-medium">Error loading bots</h3>
                            <p className="text-red-700">{error}</p>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-6">
                    {filteredBots.length > 0 ? (
                        filteredBots.map((bot) => (
                            <div key={bot.bot_id} className="bg-white rounded-xl shadow-sm overflow-hidden">
                                <div className="p-6 flex flex-col md:flex-row gap-6">
                                    {/* Bot information */}
                                    <div className="flex-shrink-0 flex flex-col items-center md:items-start">
                                        <div
                                            className="h-24 w-24 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center mb-4">
                                            {bot.icon_path ? (
                                                <img
                                                    src={`https://api.digitalaalim.com/${bot.icon_path}`}
                                                    alt={`${bot.bot_name} icon`}
                                                    className="h-full w-full object-cover"
                                                />
                                            ) : (
                                                <Bot size={48} className="text-gray-400"/>
                                            )}
                                        </div>

                                        <div className="text-center md:text-left">
                                            <h3 className="text-lg font-semibold text-gray-800">{bot.bot_name}</h3>
                                            <p className="text-sm text-gray-600">{bot.email}</p>
                                            <div className="mt-1 flex items-center">
                        <span
                            className="px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          {bot.sect}
                        </span>
                                                <span className="ml-2 text-xs text-gray-500">
                          {new Date(bot.created_at).toLocaleDateString()}
                        </span>
                                            </div>

                                            <div className="mt-3 flex items-center gap-2">
                                                <button
                                                    onClick={() => approveBot(bot.bot_id)}
                                                    disabled={bot.approved}
                                                    className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded ${
                                                        bot.approved
                                                            ? 'bg-green-50 text-green-600 cursor-not-allowed'
                                                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                                                    } transition-colors`}
                                                >
                                                    <CheckCircle size={14} className="mr-1"/>
                                                    {bot.approved ? 'Approved' : 'Approve'}
                                                </button>

                                                <button
                                                    onClick={() => suspendBot(bot.bot_id)}
                                                    disabled={!bot.approved}
                                                    className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded ${
                                                        !bot.approved
                                                            ? 'bg-gray-50 text-gray-400 cursor-not-allowed'
                                                            : 'bg-red-100 text-red-700 hover:bg-red-200'
                                                    } transition-colors`}
                                                >
                                                    <XCircle size={14} className="mr-1"/>
                                                    Suspend
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Bot update form */}
                                    <div
                                        className="flex-1 border-t md:border-t-0 md:border-l border-gray-200 pt-4 md:pt-0 md:pl-6">
                                        <h4 className="text-sm font-medium text-gray-700 mb-3">Update Bot Content</h4>

                                        <div className="space-y-4">
                                            {/* Link input */}
                                            <div>
                                                <label htmlFor={`link-${bot.bot_id}`}
                                                       className="block text-xs text-gray-500 mb-1">
                                                    External Link
                                                </label>
                                                <div className="flex items-center">
                                                    <div className="relative flex-1">
                                                        <div
                                                            className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                            <LinkIcon size={14} className="text-gray-400"/>
                                                        </div>
                                                        <input
                                                            id={`link-${bot.bot_id}`}
                                                            type="text"
                                                            placeholder="https://example.com/data.csv"
                                                            value={linkInputs[bot.bot_id] || ''}
                                                            onChange={(e) => handleLinkChange(e, bot.bot_id)}
                                                            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* File upload */}
                                            <div>
                                                <label htmlFor={`file-${bot.bot_id}`}
                                                       className="block text-xs text-gray-500 mb-1">
                                                    Upload Files
                                                </label>
                                                <div className="flex items-center">
                                                    <label
                                                        htmlFor={`file-${bot.bot_id}`}
                                                        className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded cursor-pointer transition-colors"
                                                    >
                                                        <Upload size={14} className="text-gray-600"/>
                                                        <span className="text-sm text-gray-700">Select Files</span>
                                                        <input
                                                            id={`file-${bot.bot_id}`}
                                                            type="file"
                                                            multiple
                                                            onChange={(e) => handleFileChange(e, bot.bot_id)}
                                                            className="hidden"
                                                        />
                                                    </label>
                                                </div>

                                                {/* Selected files list */}
                                                {fileInputs[bot.bot_id] && fileInputs[bot.bot_id].length > 0 && (
                                                    <ul className="mt-2 space-y-1 max-h-24 overflow-y-auto">
                                                        {fileInputs[bot.bot_id].map((file, index) => (
                                                            <li key={index}
                                                                className="flex items-center justify-between py-1 px-2 bg-gray-50 rounded text-xs">
                                                                <span
                                                                    className="truncate max-w-[200px]">{file.name}</span>
                                                                <button
                                                                    onClick={() => handleFileDelete(bot.bot_id, index)}
                                                                    className="text-red-500 hover:text-red-700"
                                                                >
                                                                    <Trash2 size={12}/>
                                                                </button>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </div>

                                            {/* Update button */}
                                            <button
                                                onClick={() => updateBot(bot.bot_id)}
                                                disabled={
                                                    botUpdating[bot.bot_id] ||
                                                    (!fileInputs[bot.bot_id] && !linkInputs[bot.bot_id]) ||
                                                    (fileInputs[bot.bot_id] && fileInputs[bot.bot_id].length === 0 && !linkInputs[bot.bot_id])
                                                }
                                                className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded text-sm font-medium transition-colors ${
                                                    botUpdating[bot.bot_id] ||
                                                    (!fileInputs[bot.bot_id] && !linkInputs[bot.bot_id]) ||
                                                    (fileInputs[bot.bot_id] && fileInputs[bot.bot_id].length === 0 && !linkInputs[bot.bot_id])
                                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                        : 'bg-blue-600 text-white hover:bg-blue-700'
                                                }`}
                                            >
                                                {botUpdating[bot.bot_id] ? (
                                                    <>
                                                        <Loader2 size={16} className="animate-spin"/>
                                                        Updating...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Bot size={16}/>
                                                        Update Bot
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Bot description */}
                                {bot.description && (
                                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                                        <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Description</h4>
                                        <p className="text-sm text-gray-700">{bot.description}</p>
                                    </div>
                                )}
                            </div>
                        ))
                    ) : (
                        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
                            {searchTerm || filterSect ? (
                                <div className="flex flex-col items-center">
                                    <Search size={40} className="text-gray-400 mb-4"/>
                                    <h3 className="text-lg font-medium text-gray-800 mb-2">No bots found</h3>
                                    <p className="text-gray-600 mb-4">
                                        No bots match your current search filters
                                    </p>
                                    <button
                                        onClick={() => {
                                            setSearchTerm('');
                                            setFilterSect('');
                                        }}
                                        className="text-blue-600 hover:text-blue-800 hover:underline"
                                    >
                                        Clear all filters
                                    </button>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center">
                                    <Bot size={40} className="text-gray-400 mb-4"/>
                                    <h3 className="text-lg font-medium text-gray-800 mb-2">No bots available</h3>
                                    <p className="text-gray-600">
                                        There are no Maktab chatbots currently registered
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ViewMaktabPage;